import { useState } from 'react';
import { beneficiosFamiliar, beneficiosPlus } from '@/mocks/coverly';

interface RegistroStep2Props {
  onNext: (plan: 'familiar' | 'plus') => void;
  onBack: () => void;
}

const planCopy = {
  familiar: {
    name: 'Basico',
    price: 289,
    people: '3 personas',
    note: 'Para familias que buscan una mensualidad accesible.',
    bg: '#FFFFFF',
    border: '#E4DFD2',
  },
  plus: {
    name: 'Prime',
    price: 399,
    people: '4 personas',
    note: 'Mas capacidad y servicios adicionales en el plan.',
    bg: '#120B25',
    border: '#120B25',
  },
};

export default function RegistroStep2({ onNext, onBack }: RegistroStep2Props) {
  const [selected, setSelected] = useState<'familiar' | 'plus'>('familiar');
  const benefits = selected === 'familiar' ? beneficiosFamiliar : beneficiosPlus;

  return (
    <div className="flex h-full flex-col bg-[#F6F4EE]">
      <div className="flex items-center gap-4 bg-[#F6F4EE] px-5 pb-4 pt-12">
        <button onClick={onBack} className="flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-sm">
          <i className="ri-arrow-left-line text-xl text-[#534AB7]" />
        </button>
        <div className="flex-1">
          <p className="text-xs font-bold uppercase tracking-wide text-[#7C7589]">Paso 2 de 3</p>
          <p className="text-sm font-bold text-[#120B25]">Elige tu plan</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 pb-28 pt-2">
        <h2 className="text-3xl font-black leading-tight text-[#120B25]">Selecciona la membresia ideal.</h2>
        <p className="mt-2 text-sm leading-6 text-[#625D6E]">
          Ambos planes dan acceso a servicios funerarios con proveedores aliados. Puedes cambiarlo con apoyo de un asesor.
        </p>

        <div className="mt-6 grid gap-3">
          <PlanButton
            plan="familiar"
            selected={selected === 'familiar'}
            onSelect={() => setSelected('familiar')}
          />
          <PlanButton
            plan="plus"
            selected={selected === 'plus'}
            onSelect={() => setSelected('plus')}
          />
        </div>

        <section className="mt-5 rounded-3xl border border-[#E4DFD2] bg-white p-4">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-[#7C7589]">Servicios incluidos</p>
              <h3 className="text-lg font-black text-[#120B25]">Plan {planCopy[selected].name}</h3>
            </div>
            <span className="rounded-full bg-[#EFEDFF] px-3 py-1 text-xs font-black text-[#534AB7]">
              {planCopy[selected].people}
            </span>
          </div>

          <ul className="grid gap-2">
            {benefits.map((benefit, index) => (
              <li key={benefit} className="flex gap-3 rounded-2xl bg-[#F8F7F2] px-3 py-2.5">
                <span className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full ${selected === 'plus' && index >= beneficiosFamiliar.length ? 'bg-[#F7B84B]' : 'bg-[#DDF3EA]'}`}>
                  <i className={`ri-check-line text-xs ${selected === 'plus' && index >= beneficiosFamiliar.length ? 'text-[#442900]' : 'text-[#1D9E75]'}`} />
                </span>
                <span className="text-sm leading-5 text-[#332D42]">{benefit}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <div className="fixed bottom-0 left-0 right-0 border-t border-[#E8E1D3] bg-white px-5 py-4" style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom, 16px))' }}>
        <button
          onClick={() => onNext(selected)}
          className="w-full rounded-2xl bg-[#534AB7] py-4 text-base font-black text-white shadow-lg shadow-[#534AB7]/20 transition-transform active:scale-[0.98]"
        >
          Continuar con Plan {planCopy[selected].name}
        </button>
      </div>
    </div>
  );
}

function PlanButton({
  plan,
  selected,
  onSelect,
}: {
  plan: 'familiar' | 'plus';
  selected: boolean;
  onSelect: () => void;
}) {
  const p = planCopy[plan];
  const dark = plan === 'plus';

  return (
    <button
      onClick={onSelect}
      className="relative rounded-3xl border-2 p-4 text-left shadow-sm transition-all active:scale-[0.99]"
      style={{
        backgroundColor: p.bg,
        borderColor: selected ? '#534AB7' : p.border,
        boxShadow: selected ? '0 0 0 4px rgba(83,74,183,0.14)' : undefined,
      }}
    >
      {plan === 'plus' && (
        <span className="absolute right-4 top-4 rounded-full bg-[#F7B84B] px-3 py-1 text-[10px] font-black uppercase text-[#3A2500]">
          Recomendado
        </span>
      )}

      <div className="flex items-start justify-between gap-4">
        <div>
          <p className={`text-sm font-black ${dark ? 'text-white' : 'text-[#120B25]'}`}>Plan {p.name}</p>
          <p className={`mt-1 text-xs leading-5 ${dark ? 'text-white/60' : 'text-[#6F6878]'}`}>{p.note}</p>
        </div>
        <span className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border ${selected ? 'border-[#534AB7] bg-[#534AB7]' : dark ? 'border-white/25' : 'border-[#D9D3C7]'}`}>
          {selected && <i className="ri-check-line text-sm text-white" />}
        </span>
      </div>

      <div className="mt-5 flex items-end justify-between">
        <div>
          <span className={`text-4xl font-black ${dark ? 'text-white' : 'text-[#120B25]'}`}>${p.price}</span>
          <span className={`text-sm font-semibold ${dark ? 'text-white/60' : 'text-[#7C7589]'}`}>/mes</span>
        </div>
        <span className={`rounded-2xl px-3 py-2 text-sm font-black ${dark ? 'bg-white/10 text-white' : 'bg-[#EFEDFF] text-[#534AB7]'}`}>
          {p.people}
        </span>
      </div>
    </button>
  );
}
